import { APP_INITIALIZER, ErrorHandler, NgModule, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformServer } from '@angular/common';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { ToastrModule } from 'ngx-toastr';

import { environment } from 'src/environments/environment';
import { ChunkLoadErrorHandler } from '~core/handlers/chunk-load-error.handler';
import { AddHeaderInterceptor } from '~core/interceptors/header.interceptor';
import { AuthService } from '~core/services/auth.service';
import { MaintenanceService } from '~core/services/maintenance.service';
import {initActivity, TrackUserActivityService} from "~core/services/track-activity.service";

import { CoreModule } from './core/core.module';
import { SharedModule } from './shared/shared.module';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { ErrorHandlerInterceptor } from './core/interceptors/error-handler.interceptor';
import { ConfigService } from './shared/services/config.service';
import { DecorTextureService } from './shared/services/decor-texture.service';
import { WrapperComponent } from './core/pages/wrapper/wrapper.component';

// Elf DevTools bridges every store write to the Redux DevTools browser extension.
// Wherever that extension is installed it deep-serialises a snapshot of every
// store on each write — including the items store, whose entities carry live
// three.js scene graphs — and the extension retains every snapshot for the life of
// the tab. That belongs in development only. Importing it lazily also keeps it out
// of the production bundle entirely, rather than merely leaving the call unmade.
// The `window` check additionally keeps it off the server-side prerender (Node).
if (!environment.production && typeof window !== 'undefined') {
	import('@ngneat/elf-devtools').then(({ devTools }) => devTools());
}

export function initialConfigLoad(configService: ConfigService, platformId: object): () => void | Promise<void> {
	// The prerendered public pages need no runtime config; skip the network call
	// on the server so the build never depends on (or blocks on) the API.
	return () => (isPlatformServer(platformId) ? Promise.resolve() : configService.load());
}

export function initMaintenance(maintenanceService: MaintenanceService, platformId: object): () => Promise<void> {
	// Skip the maintenance status probe during prerender: the fail-open default
	// (not in maintenance) lets the public routes render without an API call.
	return () => (isPlatformServer(platformId) ? Promise.resolve() : maintenanceService.init());
}

export function initDecorTextures(decorTextureService: DecorTextureService): () => Promise<void> {
	return () => decorTextureService.whenReady();
}

@NgModule({
	declarations: [AppComponent, WrapperComponent],
	imports: [
		BrowserModule,
		BrowserAnimationsModule,
		AppRoutingModule,
		CommonModule,
		SharedModule,
		ToastrModule.forRoot({
			progressBar: true,
			timeOut: 10000,
			positionClass: 'toast-bottom-right',
			closeButton: true,
		}),
		CoreModule,
		HttpClientModule
	],
	bootstrap: [AppComponent],
	providers: [
		{
			provide: ErrorHandler,
			useClass: ChunkLoadErrorHandler,
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: ErrorHandlerInterceptor,
			multi: true,
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: AddHeaderInterceptor,
			multi: true,
		},
		ConfigService,
		AuthService,
		TrackUserActivityService,
		MaintenanceService,
		{
			provide: APP_INITIALIZER,
			useFactory: initialConfigLoad,
			deps: [ConfigService, PLATFORM_ID],
			multi: true,
		},
		{
			provide: APP_INITIALIZER,
			useFactory: initMaintenance,
			deps: [MaintenanceService, PLATFORM_ID],
			multi: true,
		},
		{
			provide: APP_INITIALIZER,
			useFactory: initActivity,
			deps: [TrackUserActivityService],
			multi: true,
		},
		{
			provide: APP_INITIALIZER,
			useFactory: initDecorTextures,
			deps: [DecorTextureService],
			multi: true,
		},
	],
})
export class AppModule {}
